import React, { useEffect, useState } from 'react';
import { supabase } from '../App';

function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  
  // General Application Form State
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', coverLetter: '' });
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Assessment State
  const [activeAssessment, setActiveAssessment] = useState(null); 
  const [timeLeft, setTimeLeft] = useState(1200); 
  const [assessmentAnswers, setAssessmentAnswers] = useState({});

  const COMPANY_EMAIL = "careers@ilamitechnologies.com";

  useEffect(() => {
    async function fetchJobs() {
      try {
        setLoadingJobs(true);
        const { data, error } = await supabase.from('jobs').select('*').eq('is_active', true).order('created_at', { ascending: false });
        if (error) throw error;
        setJobs(data || []);
      } catch (e) {
        setJobs([
          { id: 1, title: "Senior React Developer", department: "Engineering", location: "Remote", type: "Full-Time" },
          { id: 2, title: "Network Architect", department: "Infrastructure", location: "Durban, ZA", type: "Contract" }
        ]);
      } finally {
        setLoadingJobs(false);
      }
    }
    fetchJobs();
  }, []);

  useEffect(() => {
    let timer;
    if (activeAssessment && timeLeft > 0) {
      timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    } else if (timeLeft === 0) {
      alert("Time is up! The assessment has been locked.");
      setActiveAssessment(null); 
    }
    return () => clearInterval(timer);
  }, [activeAssessment, timeLeft]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.remove('opacity-0', 'translate-y-12');
          entry.target.classList.add('opacity-100', 'translate-y-0');
          observer.unobserve(entry.target);
        }
      }),
      { threshold: 0.15 }
    );
    const hiddenElements = document.querySelectorAll('.animate-on-scroll');
    hiddenElements.forEach((el) => observer.observe(el));
    return () => hiddenElements.forEach((el) => observer.unobserve(el));
  }, [jobs, showForm]);

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v' || e.key === 'x')) {
      e.preventDefault();
      alert("Clipboard actions are disabled during the assessment.");
    }
  };

  const startAssessment = (job) => {
    setActiveAssessment(job);
    setTimeLeft(1200); 
    setAssessmentAnswers({});
  };

  const submitAssessment = () => {
    alert("Assessment submitted! If you meet our baseline, your application email will now open.");
    
    const subject = encodeURIComponent(`Assessment Passed: Application for ${activeAssessment.title}`);
    const body = encodeURIComponent(
      `I have completed the technical assessment.\n\n` +
      `Please find my CV attached.\n\n` +
      `[Write your brief introduction here]`
    );
    window.location.href = `mailto:${COMPANY_EMAIL}?subject=${subject}&body=${body}`;
    setActiveAssessment(null);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleGeneralSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', msg: '' });

    try {
      const subject = encodeURIComponent("General Application - iLAMI Technologies");
      const body = encodeURIComponent(
        `Name: ${formData.name}\n` +
        `Email: ${formData.email}\n\n` +
        `Cover Letter / Pitch:\n${formData.coverLetter}\n\n` +
        `[Please attach your CV/Resume to this email before sending]`
      );

      window.location.href = `mailto:${COMPANY_EMAIL}?subject=${subject}&body=${body}`;

      setStatus({ type: 'success', msg: 'Opening your email client... Please attach your CV and hit send!' });
      
      setTimeout(() => {
        setShowForm(false);
        setFormData({ name: '', email: '', coverLetter: '' });
        setIsSubmitting(false);
      }, 4000); 
      
    } catch (error) {
      setStatus({ type: 'error', msg: 'System error. Please try again later.' });
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <div className="min-h-screen pt-24 pb-20 px-6 relative">
      
      {activeAssessment && (
        <div className="fixed inset-0 z-[200] bg-slate-950 flex flex-col">
          <div className="bg-slate-900 border-b border-slate-800 p-4 flex justify-between items-center shadow-lg">
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">Technical Assessment</h2>
              <p className="text-blue-400 text-sm">{activeAssessment.title}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className={`text-2xl font-mono font-bold ${timeLeft < 300 ? 'text-red-500 animate-pulse' : 'text-slate-200'}`}>
                {formatTime(timeLeft)}
              </div>
              <button 
                onClick={() => setActiveAssessment(null)}
                className="text-slate-400 hover:text-white text-sm uppercase tracking-widest border border-slate-700 px-4 py-2 rounded-lg"
              >
                Abort
              </button>
            </div>
          </div>

          <div 
            className="flex-grow overflow-y-auto p-8 max-w-4xl mx-auto w-full select-none"
            onContextMenu={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onPaste={(e) => e.preventDefault()}
            onKeyDown={handleKeyDown}
          >
            <div className="bg-slate-900/50 border border-red-900/30 p-4 rounded-lg mb-8">
              <p className="text-red-400 text-sm font-bold flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                Anti-Cheat Enabled: Highlighting, right-clicking, and clipboard pasting are strictly disabled.
              </p>
            </div>

            <div className="space-y-8">
              <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl">
                <h3 className="text-white font-bold mb-4">Question 1: Architectural Design</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  Explain how you would structure a highly scalable microservices architecture using Docker, Kubernetes, and an API Gateway. How do you handle database transactions across multiple independent services?
                </p>
                <textarea 
                  rows="6" 
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg p-4 text-sm text-white focus:border-blue-500 outline-none resize-none"
                  placeholder="Type your original answer here..."
                  value={assessmentAnswers.q1 || ''}
                  onChange={(e) => setAssessmentAnswers({...assessmentAnswers, q1: e.target.value})}
                ></textarea>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button 
                onClick={submitAssessment}
                className="bg-blue-600 text-white px-10 py-4 rounded-lg font-bold hover:bg-blue-500 shadow-[0_0_20px_rgba(37,99,235,0.2)] transition-all"
              >
                Submit Assessment & Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`max-w-4xl mx-auto ${activeAssessment ? 'hidden' : 'block'}`}>
        <div className="relative overflow-hidden rounded-2xl bg-slate-900/60 backdrop-blur-md border border-slate-800 p-10 md:p-14 mb-10 shadow-lg animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1000ms] ease-out text-center md:text-left">
          <div className="absolute inset-0 z-0">
            <img src="/assets/images/background01.png" className="w-full h-full object-cover grayscale opacity-[0.05]" alt="Careers" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/50 to-transparent"></div>
          </div>
          <div className="relative z-10">
            <p className="text-blue-500 text-xs uppercase tracking-[0.3em] font-bold mb-3">Join the Team</p>
            <h1 className="text-4xl md:text-5xl font-black text-white tracking-tighter mb-4">Careers at iLAMI</h1>
            <p className="text-slate-300 text-lg font-medium leading-relaxed max-w-2xl mt-2 mx-auto md:mx-0">
            We are always looking for exceptional talent to help us architect secure, scalable IT solutions for the modern enterprise.
            Pass our rigorous technical assessments to prove you have what it takes to join iLami Technologies.
            </p>
          </div>
        </div>

        <div 
          className="bg-slate-900/60 backdrop-blur-md border border-slate-800 p-10 rounded-2xl mb-8 shadow-lg animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1000ms] ease-out"
          style={{ transitionDelay: '150ms' }}
        >
          <div className="flex items-center gap-4 mb-6 border-b border-slate-800 pb-4">
            <div className="p-2 bg-blue-900/30 rounded-lg text-blue-500">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
            </div>
            <h2 className="text-2xl font-bold text-white tracking-tight">Current Openings</h2>
          </div>
          
          {loadingJobs ? (
            <div className="py-12 flex flex-col items-center justify-center text-center bg-slate-950/50 rounded-xl border border-slate-800/50 shadow-inner">
              <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-4"></div>
            </div>
          ) : jobs.length > 0 ? (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div key={job.id} className="bg-slate-950/50 border border-slate-800 p-6 rounded-xl hover:border-blue-500/50 transition-all duration-300 group flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-inner">
                  <div>
                    <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">{job.title}</h3>
                    <p className="text-slate-400 text-xs tracking-wider uppercase mt-1">{job.department} &bull; {job.location} &bull; {job.type}</p>
                  </div>
                  <button 
                    onClick={() => startAssessment(job)}
                    className="text-blue-400 text-sm font-bold border border-blue-400/30 px-6 py-2 rounded hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all text-center"
                  >
                    Take Assessment
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center bg-slate-950/50 rounded-xl border border-slate-800/50 shadow-inner">
              <p className="text-slate-400 text-sm">No active positions right now.</p>
            </div>
          )}
        </div>

        {/* RESTORED: GENERAL APPLICATION FORM */}
        <div 
          className="bg-slate-900/60 backdrop-blur-md border border-slate-800 p-10 rounded-2xl shadow-lg relative overflow-hidden animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1000ms] ease-out group"
          style={{ transitionDelay: '300ms' }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none group-hover:bg-blue-600/10 transition-colors duration-700"></div>

          <div className="relative z-10 text-center">
            {!showForm ? (
              <>
                <h2 className="text-2xl font-bold text-white mb-3 tracking-tight">Don't see a perfect fit?</h2>
                <p className="text-slate-400 text-sm mb-8 max-w-md mx-auto leading-relaxed">
                  We are constantly growing. Submit your details to our talent network and we will reach out when a role aligns with your expertise.
                </p>
                <button 
                  onClick={() => setShowForm(true)}
                  className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-500 hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] active:scale-95 transition-all duration-300 text-sm uppercase tracking-widest"
                >
                  Submit General Application
                </button>
              </>
            ) : (
              <form onSubmit={handleGeneralSubmit} className="text-left max-w-lg mx-auto space-y-4 animate-on-scroll opacity-0 translate-y-4">
                <h3 className="text-xl font-bold text-white mb-4 border-b border-slate-800 pb-2">General Application</h3>
                
                {status.msg && (
                  <div className={`p-3 rounded-lg text-xs font-bold border ${status.type === 'success' ? 'bg-blue-900/30 text-blue-400 border-blue-800' : 'bg-red-900/30 text-red-400 border-red-800'} transition-all duration-300`}>
                    {status.msg}
                  </div>
                )}

                <div>
                  <label className="block text-slate-400 text-xs font-bold mb-1 ml-1 uppercase">Full Name</label>
                  <input type="text" name="name" required value={formData.name} onChange={handleChange} disabled={isSubmitting} className="w-full bg-slate-950/80 border border-slate-800 rounded-lg p-3 text-sm text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-inner outline-none disabled:opacity-50" />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs font-bold mb-1 ml-1 uppercase">Email Address</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} disabled={isSubmitting} className="w-full bg-slate-950/80 border border-slate-800 rounded-lg p-3 text-sm text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 shadow-inner outline-none disabled:opacity-50" />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs font-bold mb-1 ml-1 uppercase">Cover Letter / Pitch</label>
                  <textarea rows="4" name="coverLetter" required value={formData.coverLetter} onChange={handleChange} disabled={isSubmitting} className="w-full bg-slate-950/80 border border-slate-800 rounded-lg p-3 text-sm text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none shadow-inner outline-none disabled:opacity-50" placeholder="Tell us why you belong at iLAMI..."></textarea>
                </div>
                
                <div className="flex gap-4 pt-2">
                  <button type="submit" disabled={isSubmitting} className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-500 transition-all text-sm uppercase tracking-wider flex justify-center items-center gap-2 disabled:opacity-70">
                    {isSubmitting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : 'Prepare Email'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} disabled={isSubmitting} className="px-6 bg-slate-800 text-slate-300 py-3 rounded-lg font-bold hover:bg-slate-700 transition-all text-sm uppercase disabled:opacity-50">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default Careers;